import React, { useMemo, useState } from 'react';
import { ArrowUpRight, GraduationCap, FileText, BookOpen, Rocket, Search } from 'lucide-react';
import PageHead from '../components/PageHead';
import { growthNote, certsCourses, articles, books, projects } from '../data/portfolio';

const tabs = [
  { key: 'certs', label: 'Certs / Course', icon: GraduationCap },
  { key: 'articles', label: 'Article', icon: FileText },
  { key: 'books', label: 'Books', icon: BookOpen },
  { key: 'projects', label: 'Project', icon: Rocket }
];

const Row = ({ date, title, desc, link, muted, openInNewTab }) => (
  <a
    href={link || '#'}
    target={openInNewTab ? '_blank' : undefined}
    rel={openInNewTab ? 'noreferrer' : undefined}
    className="group grid grid-cols-[110px_1fr_auto] items-start gap-4 py-4 border-b border-border/60 hover:bg-brand/10 hover:border-brand/40 hover:shadow-md hover:-translate-y-0.5 px-2 -mx-2 rounded-md transition-all"
  >
    <div className="mono text-[11.5px] uppercase tracking-widest text-ink-mute pt-0.5">{date}</div>
    <div>
      <div className="text-[14.5px] font-medium text-foreground">{title}</div>
      {desc && <div className="text-[13px] text-ink-soft mt-0.5">{desc}</div>}
    </div>
    {link && !muted && (
      <ArrowUpRight
        size={16}
        className="text-ink-mute group-hover:text-brand group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform mt-1"
      />
    )}
  </a>
);

const Growth = () => {
  const [tab, setTab] = useState('certs');
  const [q, setQ] = useState('');

  // Dynamic counters computed from data lists in `src/data/portfolio.js`
  const dynamicCounts = {
    'Certs / Courses': certsCourses.length,
    'Articles Published': articles.length,
    'Executive Books Read': books.length,
    'Projects Shipped': projects.length,
  };

  const displayCounters = growthNote.counters.map((c) => {
    const raw = dynamicCounts[c.l];
    const count = typeof raw === 'number' ? raw : parseInt(String(c.n || '').replace(/\D/g, ''), 10) || 0;
    return { ...c, n: `${String(count).padStart(2, '0')}+` };
  });

  const rows = () => {
    if (tab === 'certs') return certsCourses.map((c) => ({ date: c.date, title: c.title, desc: c.desc, link: c.link, openInNewTab: !!c.link }));
    if (tab === 'articles')
      return articles.map((a) => {
        // If the article link contains an internal hash route (#/writing/...),
        // convert it to a site-local hash so it navigates within the SPA.
        const hasInternalHash = typeof a.link === 'string' && a.link.includes('#/writing');
        const link = hasInternalHash ? a.link.substring(a.link.indexOf('#')) : a.link;
        const openInNewTab = !!link && !hasInternalHash;
        return { date: a.date, title: a.title, desc: a.sub, link, openInNewTab };
      });
      if (tab === 'books') return books.map((b) => ({ date: b.date, title: b.title, desc: null, link: b.link, openInNewTab: !!b.link }));
      return projects.map((p) => ({ date: p.date, title: p.title, desc: p.body, link: p.link, openInNewTab: !!p.link }));
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const all = rows();
    if (!term) return all;
    return all.filter((r) => [r.title, r.desc, r.date].some((f) => (f || '').toLowerCase().includes(term)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, tab]);

  return (
    <div className="max-w-3xl mx-auto px-6 pb-8">
      <PageHead eyebrow={growthNote.eyebrow} title={growthNote.title} />

      <p className="text-[16px] text-ink-soft mt-2 mb-10 max-w-2xl leading-[1.8]">{growthNote.body}</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
        {displayCounters.map((c) => (
          <div key={c.l} className="group rounded-xl border border-border p-5 bg-card/40 hover:bg-card/70 hover:border-brand/60 hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <div className="text-2xl font-medium tracking-tight text-foreground">
              {c.n.replace(/\+$/, '')}
              <span className="serif-italic text-brand text-xl">+</span>
            </div>
            <div className="mono uppercase tracking-widest text-[10.5px] text-ink-mute mt-2">{c.l}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4 sticky top-16 bg-background/80 backdrop-blur py-3 z-20">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-2 h-9 px-4 rounded-lg text-[13px] transition-all ${
              tab === t.key
                ? 'bg-brand text-black border border-brand'
                : 'border border-border text-ink-soft hover:bg-brand/10 hover:text-foreground hover:border-brand/60 hover:shadow-md hover:-translate-y-0.5'
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-mute pointer-events-none"
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${tabs.find((t) => t.key === tab)?.label.toLowerCase()}…`}
          className="w-full h-11 pl-11 pr-4 rounded-lg bg-card/40 border border-border text-[14px] focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/25 transition-colors"
        />
        {q && (
          <button
            onClick={() => setQ('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] mono uppercase tracking-widest text-ink-mute hover:text-foreground px-2 py-1"
          >
            clear
          </button>
        )}
      </div>

      <div className="mono uppercase tracking-widest text-[10.5px] text-ink-mute mb-2">
        {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
      </div>

      <div>
        {filtered.map((r, i) => (
          <Row key={i} {...r} />
        ))}
        {filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-ink-mute text-[13.5px] mt-4">
            No entries match “{q}”.
          </div>
        )}
      </div>
    </div>
  );
};

export default Growth;
